import { TableCell, TableRow } from "@mui/material";
import type { FC } from "react";
import { useTranslation, type UseTranslationResponse } from "react-i18next";

import {
  EMPTY_VALUE_DISPLAY,
  formatDateDMY,
  formatTimeISO,
  timezoneAdjustedDateToDayStartISO,
  useFormats,
  useTimezone,
  type BookingDto,
  type ErrorAdditionalT,
  type IApprovalBookingConflict,
  type TimeZoneEnum,
} from "@ryde/shared";

import { RejectedBookingPassengerTableCell } from "./rejected-booking-passenger-table-cell";

interface IRejectedBookingsInsideBookingRowProps {
  conflictRow: IApprovalBookingConflict;
}

interface IGetInsideBookingReasonTranslationKeyByBookingConflictTypeArguments {
  conflictRow: IApprovalBookingConflict;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: UseTranslationResponse<any, any>["t"];
  timeFormat: string;
  timezone: TimeZoneEnum;
}

const getInsideBookingReasonTranslationKeyByBookingConflictType = ({
  conflictRow,
  t,
  timeFormat,
  timezone,
}: IGetInsideBookingReasonTranslationKeyByBookingConflictTypeArguments): string => {
  const errorAdditionalCasted = conflictRow.errorAdditional as Record<
    ErrorAdditionalT,
    BookingDto
  >;

  return t(`booking.bulk.conflictInsideTheBooking`, {
    bookingId: errorAdditionalCasted?.insideBooking?.id ?? EMPTY_VALUE_DISPLAY,
    bookingDirection:
      errorAdditionalCasted?.insideBooking?.direction ?? EMPTY_VALUE_DISPLAY,
    bookTime: (errorAdditionalCasted?.insideBooking as BookingDto)?.bookTime
      ? formatTimeISO({
          dateTimeISO: (errorAdditionalCasted?.insideBooking as BookingDto)
            ?.bookTime,
          timeFormat,
          timezone,
        })
      : EMPTY_VALUE_DISPLAY,
    originStationName:
      (errorAdditionalCasted?.insideBooking as BookingDto)?.originStation
        ?.name ?? EMPTY_VALUE_DISPLAY,
    destinationStationName:
      (errorAdditionalCasted?.insideBooking as BookingDto)?.destinationStation
        ?.name ?? EMPTY_VALUE_DISPLAY,
  });
};

export const RejectedBookingsInsideBookingRow: FC<
  IRejectedBookingsInsideBookingRowProps
> = ({ conflictRow }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { timeFormat } = useFormats();
  return (
    <TableRow>
      <TableCell>{conflictRow.passengerId}</TableCell>
      <RejectedBookingPassengerTableCell passengerId={conflictRow.passengerId}>
        {conflictRow.passengerFullName}
      </RejectedBookingPassengerTableCell>
      <TableCell>
        {getInsideBookingReasonTranslationKeyByBookingConflictType({
          conflictRow,
          t,
          timeFormat,
          timezone,
        })}
      </TableCell>
      <TableCell>
        {formatDateDMY(
          timezoneAdjustedDateToDayStartISO(conflictRow.date, timezone),
          timezone,
        )}
      </TableCell>
    </TableRow>
  );
};
